import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Search, ChevronLeft, ChevronRight, Download, Mail, Filter, X, ChevronDown } from 'lucide-react';
import type { Result, Event, ColumnConfig } from '../types';
import ResultModal from './ResultModal';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import LiquidGlass from './LiquidGlass';

const RESULTS_PER_PAGE = 20;

export default function Results() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [selectedResult, setSelectedResult] = useState<Result | null>(null);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([]);
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [showSubEventDropdown, setShowSubEventDropdown] = useState(false);
  const [selectedSubEvent, setSelectedSubEvent] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadEvents();
    }
  }, [user, organization]);

  useEffect(() => {
    if (selectedEvent) {
      loadResults();
      loadColumnConfig();
      const event = events.find(e => e.id === selectedEvent);
      if (event) {
        setCurrentEvent(event);
      }
    }
  }, [selectedEvent, currentPage]);

  useEffect(() => {
    if (selectedEvent) {
      loadResults();
    }
  }, [selectedSubEvent]);

  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
      const delayDebounce = setTimeout(() => {
        loadResults();
      }, 300);

      return () => clearTimeout(delayDebounce);
    } else if (selectedEvent) {
      loadResults();
    }
  }, [searchTerm]);

  const loadEvents = async () => {
    if (!user || !organization) return;
    
    try {
      setLoadingEvents(true);
      
      // First, clear any existing events to prevent flashing
      setEvents([]);
      
      let query = supabase
        .from('events')
        .select('*');

      // If user belongs to an organization, fetch both personal and organization events
      if (organization?.id) {
        query = query.or(`user_id.eq.${user.id},organization_id.eq.${organization.id}`);
      } else {
        // Otherwise, just fetch personal events
        query = query.eq('user_id', user.id);
      }

      const { data } = await query.order('date', { ascending: false });

      const eventsData = data || [];
      setEvents(eventsData);
      
      if (eventsData.length > 0) {
        // Only set selected event if not already set or if current selection is not in the new data
        if (!selectedEvent || !eventsData.some(e => e.id === selectedEvent)) {
          setSelectedEvent(eventsData[0].id);
          setCurrentEvent(eventsData[0]);
          setEventSearchTerm(eventsData[0].name);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading events:', error);
      setLoading(false);
    } finally {
      setLoadingEvents(false);
      setInitialLoadComplete(true);
    }
  };

  const loadColumnConfig = async () => {
    try {
      const { data: event } = await supabase
        .from('events')
        .select('column_config, sub_event_config')
        .eq('id', selectedEvent)
        .single();

      if (event?.column_config) {
        // Ensure visibleOnMobile property exists for all columns
        const updatedConfig = event.column_config.map((col: ColumnConfig) => ({
          ...col,
          visibleOnMobile: col.visibleOnMobile !== undefined ? 
            col.visibleOnMobile : 
            ['rank', 'name', 'chip_time'].includes(col.type)
        }));
        setColumnConfig(updatedConfig);
      }
    } catch (err) {
      console.error('Error loading column config:', err);
    }
  };

  const loadResults = async () => {
    if (!selectedEvent) return;
    
    setLoadingMore(true);
    let query = supabase
      .from('results')
      .select('*', { count: 'exact' })
      .eq('event_id', selectedEvent);

    if (selectedSubEvent) {
      query = query.eq('sub_event', selectedSubEvent);
    }

    if (searchTerm) {
      query = query.or(
        `bib_number.ilike.%${searchTerm}%,` +
        `name.ilike.%${searchTerm}%,` +
        `club.ilike.%${searchTerm}%,` +
        `custom_1.ilike.%${searchTerm}%,` +
        `custom_2.ilike.%${searchTerm}%`
      );
    }

    query = query
      .order('rank', { ascending: true, nullsFirst: false })
      .order('upload_order_index', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .range((currentPage - 1) * RESULTS_PER_PAGE, currentPage * RESULTS_PER_PAGE - 1);

    const { data, count, error } = await query;

    if (error) {
      console.error('Error loading results:', error);
    } else {
      setResults(data || []);
      setTotalResults(count || 0);
    }
    setLoadingMore(false);
  };

  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);

  const goToPage = (page: number) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredEvents = events.filter(event => 
    event.name.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
    formatDate(event.date).includes(eventSearchTerm)
  );

  // Get visible columns based on configuration
  const getVisibleColumns = (screenSize: 'mobile' | 'tablet' | 'desktop') => {
    const visibleColumns = columnConfig
      .filter(col => col.visible && col.type !== 'split_point');

    if (screenSize === 'mobile') {
      // On mobile, only show columns marked as visibleOnMobile
      return visibleColumns.filter(col => col.visibleOnMobile === true);
    } else {
      // On desktop and tablet, show all visible columns
      return visibleColumns;
    }
  };

  const getSelectedSubEventDisplayName = () => {
    if (!selectedSubEvent || !currentEvent?.sub_event_config) return 'All Sub Events';
    const event = currentEvent.sub_event_config.events.find(e => e.value === selectedSubEvent);
    return event ? event.displayName : 'All Sub Events';
  };

  if (loading || loadingEvents) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-800">Results</h1>
      </div>

      <div className="space-y-4">
        {/* Event Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search events..."
            value={eventSearchTerm}
            onChange={(e) => {
              setEventSearchTerm(e.target.value);
              setShowEventDropdown(true);
            }}
            onFocus={() => {
              setEventSearchTerm('');
              setShowEventDropdown(true);
            }}
            className="block w-full pl-10 pr-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg"
          />
          {showEventDropdown && (
            <LiquidGlass className="absolute z-10 mt-1 w-full max-h-60 overflow-auto">
              {filteredEvents.map((event) => (
                <button
                  key={event.id}
                  onClick={() => {
                    setSelectedEvent(event.id);
                    setEventSearchTerm(event.name);
                    setShowEventDropdown(false);
                    setCurrentPage(1);
                    setSelectedSubEvent(null);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-white/20 flex justify-between items-center text-gray-800 transition-colors duration-200"
                >
                  <span className="font-medium">{event.name}</span>
                  <span className="text-sm text-gray-600">{formatDate(event.date)}</span>
                </button>
              ))}
              {filteredEvents.length === 0 && (
                <div className="px-4 py-2 text-sm text-gray-600">
                  No events found
                </div>
              )}
            </LiquidGlass>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          {/* Sub Event Selector */}
          {currentEvent?.sub_event_config && (
            <div className="relative">
              <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                <button
                  onClick={() => setShowSubEventDropdown(!showSubEventDropdown)}
                  className="w-full sm:w-48 pl-4 pr-10 py-2 text-white font-medium text-left flex items-center justify-between"
                >
                  <span className="truncate">{getSelectedSubEventDisplayName()}</span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0 ml-2" />
                </button>
              </LiquidGlass>
              {showSubEventDropdown && (
                <LiquidGlass className="absolute z-10 mt-1 w-full">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setSelectedSubEvent(null);
                        setCurrentPage(1);
                        setShowSubEventDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-white/20 text-gray-800 transition-colors duration-200"
                    >
                      All Sub Events
                    </button>
                    {currentEvent.sub_event_config.events.map((event, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedSubEvent(event.value);
                          setCurrentPage(1);
                          setShowSubEventDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-white/20 text-gray-800 transition-colors duration-200"
                      >
                        {event.displayName}
                      </button>
                    ))}
                  </div>
                </LiquidGlass>
              )}
            </div>
          )}

          {/* Results Search */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, bib number and more..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="block w-full px-3 py-2 bg-white/60 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-lg"
            />
          </div>
        </div>

        <div className="text-xs sm:text-sm text-gray-600">
          {totalResults > 0 && (
            <span>Found {totalResults} result{totalResults !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      <LiquidGlass variant="card" className="overflow-hidden">
        <div className="overflow-x-auto">
          {/* Mobile View */}
          <table className="min-w-full divide-y divide-gray-200 md:hidden">
            <thead className="bg-gray-50/80">
              <tr>
                {getVisibleColumns('mobile').map(column => (
                  <th 
                    key={column.id}
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.type === 'chip_time' && currentEvent?.show_gun_time_instead_of_chip_time 
                      ? 'Gun Time'
                      : column.type === 'gun_time' && currentEvent?.show_gun_time_instead_of_chip_time
                        ? 'Chip Time'
                        : column.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white/60 divide-y divide-gray-200">
              {results.map((result) => (
                <tr
                  key={result.id}
                  onClick={() => setSelectedResult(result)}
                  className="hover:bg-white/80 cursor-pointer transition-colors duration-200"
                >
                  {getVisibleColumns('mobile').map(column => (
                    <td 
                      key={column.id}
                      className="px-3 py-2 whitespace-nowrap text-xs text-gray-900"
                    >
                      {column.type === 'club' && result.club_position ? (
                        <span>
                          {result[column.type]}
                          <span className="text-gray-500 ml-1">
                            (#{result.club_position})
                          </span>
                        </span>
                      ) : (
                        column.type === 'chip_time' && currentEvent?.show_gun_time_instead_of_chip_time 
                          ? (result.gun_time || result.chip_time) 
                          : column.type === 'gun_time' && currentEvent?.show_gun_time_instead_of_chip_time
                            ? (result.chip_time || '-')
                            : column.type === 'custom_1' || column.type === 'custom_2'
                              ? (result[column.type] || '-')
                              : result[column.type]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={getVisibleColumns('mobile').length} className="px-3 py-4 text-center text-xs text-gray-500">
                    No results found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Desktop View */}
          <table className="min-w-full divide-y divide-gray-200 hidden md:table">
            <thead className="bg-gray-50/80">
              <tr>
                {getVisibleColumns('desktop').map(column => (
                  <th 
                    key={column.id}
                    className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.type === 'chip_time' && currentEvent?.show_gun_time_instead_of_chip_time 
                      ? 'Gun Time'
                      : column.type === 'gun_time' && currentEvent?.show_gun_time_instead_of_chip_time
                        ? 'Chip Time'
                        : column.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white/60 divide-y divide-gray-200">
              {results.map((result) => (
                <tr
                  key={result.id}
                  onClick={() => setSelectedResult(result)}
                  className="hover:bg-white/80 cursor-pointer transition-colors duration-200"
                >
                  {getVisibleColumns('desktop').map(column => (
                    <td 
                      key={column.id}
                      className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900"
                    >
                      {column.type === 'club' && result.club_position ? (
                        <span>
                          {result[column.type]}
                          <span className="text-gray-500 ml-1">
                            (#{result.club_position})
                          </span>
                        </span>
                      ) : (
                        column.type === 'chip_time' && currentEvent?.show_gun_time_instead_of_chip_time 
                          ? (result.gun_time || result.chip_time) 
                          : column.type === 'gun_time' && currentEvent?.show_gun_time_instead_of_chip_time
                            ? (result.chip_time || '-')
                            : column.type === 'custom_1' || column.type === 'custom_2'
                              ? (result[column.type] || '-')
                              : result[column.type]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={getVisibleColumns('desktop').length} className="px-3 sm:px-6 py-4 text-center text-xs sm:text-sm text-gray-500">
                    No results found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-3 sm:px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50/60">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1 || loadingMore}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages || loadingMore}
                className="relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {Math.min((currentPage - 1) * RESULTS_PER_PAGE + 1, totalResults)}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * RESULTS_PER_PAGE, totalResults)}
                  </span>{' '}
                  of <span className="font-medium">{totalResults}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1 || loadingMore}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;
                    
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => goToPage(pageNumber)}
                          disabled={loadingMore}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            isCurrentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } ${loadingMore ? 'opacity-50' : ''}`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                    
                    if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <span
                          key={pageNumber}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    
                    return null;
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages || loadingMore}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </LiquidGlass>

      {selectedResult && currentEvent && (
        <ResultModal
          result={selectedResult}
          event={currentEvent}
          onClose={() => setSelectedResult(null)}
        />
      )}
    </div>
  );
}